import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Divider, Typography, useTheme, IconButton, Tooltip } from '@mui/material';
import { Refresh as RetryIcon } from '@mui/icons-material';
import { MessageListProps } from '../../types/message';
import { MessageItem } from './MessageItem';
import { TypingIndicator } from './TypingIndicator';
import { Loading } from '../UI/Loading';

/**
 * メッセージ一覧表示コンポーネント
 * チャット履歴の表示とスクロール管理を担当
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  expert,
  isLoading = false,
  isTyping = false,
  isRegenerating = false,
  regeneratingMessageId,
  onRetry,
  onRetryAborted,
  messageStatuses,
  inputHeight = 96, // デフォルトの入力欄高さ（8 theme.spacing * 12）
}) => {
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

  // 中断されたメッセージを検出
  const getAbortedMessage = useCallback(() => {
    if (!messageStatuses) return null;
    
    // ステータスから中断されたメッセージを探す
    const abortedMessageEntry = Object.entries(messageStatuses).find(
      ([_, status]) => status.status === 'aborted'
    );
    
    if (!abortedMessageEntry) return null;
    
    const [messageIdStr, status] = abortedMessageEntry;
    const messageId = parseInt(messageIdStr);
    const message = messages.find(m => m.id === messageId);
    
    return message ? { message, status } : null;
  }, [messageStatuses, messages]);

  const abortedMessageData = getAbortedMessage();

  // 改良された自動スクロール関数
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current && isAutoScrollEnabled) {
      // シンプルにmessagesEndRefをスクロール位置に
      messagesEndRef.current.scrollIntoView({ 
        behavior,
        block: 'end'
      });
    }
  }, [isAutoScrollEnabled]);

  // スクロール位置を監視
  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const threshold = 100;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      setIsAutoScrollEnabled(isNearBottom);
    }
  };

  // 新しいメッセージやタイピング状態の変化で自動スクロール
  useEffect(() => {
    // 少し遅延を入れてDOMの更新を待つ
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(timer);
  }, [messages.length, isTyping, scrollToBottom]);

  // タイピング状態が開始されたときの即座スクロール
  useEffect(() => {
    if (isTyping) {
      // 少し遅延を入れてTypingIndicatorのレンダリングを待つ
      const timer = setTimeout(() => {
        scrollToBottom('smooth');
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isTyping, scrollToBottom]);

  // 入力欄高さ変更時の再スクロール
  useEffect(() => {
    if (isAutoScrollEnabled) {
      scrollToBottom('auto');
    }
  }, [inputHeight, isAutoScrollEnabled, scrollToBottom]);

  // 日付でメッセージをグループ化
  const groupMessagesByDate = () => {
    const groups: { [key: string]: typeof messages } = {};
    
    messages.forEach(message => {
      const date = new Date(message.createdAt);
      const dateKey = date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate();
  const shouldShowDateSeparators = messages.length > 3; // 3つ以上のメッセージがある場合のみ日付区切りを表示

  if (isLoading && messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
        }}
      >
        <Loading size="medium" />
      </Box>
    );
  }

  if (messages.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          padding: theme.spacing(4),
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.grey[500],
            marginBottom: theme.spacing(1),
          }}
        >
          まだメッセージがありません
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.grey[400],
          }}
        >
          下のテキストボックスからメッセージを送信してチャットを開始しましょう
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        padding: `${theme.spacing(1)} 0 0 0`, // 上部のみパディング
        // カスタムスクロールバー
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.grey[100],
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: theme.palette.grey[500],
          },
        },
      }}
    >
      {Object.entries(messageGroups).map(([date, groupMessages]) => (
        <Box key={date}>
          {/* 日付区切り - 複数メッセージがある場合のみ表示 */}
          {shouldShowDateSeparators && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                margin: theme.spacing(2, 0),
                gap: theme.spacing(2),
              }}
            >
              <Divider sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.grey[500],
                  backgroundColor: theme.palette.background.paper,
                  padding: theme.spacing(0.5, 1),
                  borderRadius: theme.shape.borderRadius,
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {date}
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>
          )}

          {/* メッセージ群 */}
          {groupMessages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              expert={expert}
              isRegenerating={isRegenerating && regeneratingMessageId === message.id}
              onRetry={onRetry ? () => onRetry(message.id) : undefined}
            />
          ))}
        </Box>
      ))}

      {/* 入力中インジケーター */}
      <TypingIndicator isVisible={isTyping} expertName={expert?.name} />

      {/* 中断表示インジケーター - AI側のレイアウト */}
      {abortedMessageData && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: theme.spacing(2),
            marginBottom: theme.spacing(1),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.palette.grey[100],
              borderRadius: theme.shape.borderRadius * 2,
              padding: theme.spacing(1.5, 2),
              maxWidth: '400px',
              border: `1px solid ${theme.palette.grey[200]}`,
              gap: 2,
              ...(theme.palette.mode === 'dark' && {
                backgroundColor: theme.palette.grey[800],
                borderColor: theme.palette.grey[700],
              }),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[600],
                fontSize: '0.875rem',
                fontWeight: 500,
                flex: 1,
                ...(theme.palette.mode === 'dark' && {
                  color: theme.palette.grey[400],
                }),
              }}
            >
              この回答を停止しました
            </Typography>
            
            <Tooltip title="やり直す">
              <IconButton
                onClick={() => onRetryAborted?.(abortedMessageData.message.id)}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '20',
                  },
                }}
              >
                <RetryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* スクロール位置の基準点と下部スペース */}
      <Box 
        sx={{ 
          height: `${Math.max(inputHeight - 40, 60)}px`, // 余白を減らして適切なサイズに
          flexShrink: 0 
        }}
      >
        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};